import React from 'react';
import { Link } from 'react-router-dom';

interface ScheduleTourSectionProps {
    project: any;
    getImageUrl: (url: string) => string;
    handleTourSubmit: (e: React.FormEvent) => void;
    tourDate: Date;
    setTourDate: (date: Date) => void;
    generateTourDates: () => Date[];
    tourForm: {
        time: string;
        name: string;
        phone: string;
        email: string;
        message: string;
    };
    setTourForm: React.Dispatch<React.SetStateAction<any>>;
    tourSubmitting: boolean;
    tourSuccess: string;
    tourError: string;
    termsAccepted: boolean;
    setTermsAccepted: (accepted: boolean) => void;
    tourType: 'In Person' | 'Video Chat';
    setTourType: (type: 'In Person' | 'Video Chat') => void;
}

const ScheduleTourSection: React.FC<ScheduleTourSectionProps> = ({
    project,
    getImageUrl,
    handleTourSubmit,
    tourDate,
    setTourDate,
    generateTourDates,
    tourForm,
    setTourForm,
    tourSubmitting,
    tourSuccess,
    tourError,
    termsAccepted,
    setTermsAccepted,
    tourType,
    setTourType
}) => {
    return (
        <div id="schedule-tour" className="mb-12 mt-8 scroll-mt-24">
            <div className="card border border-gray-100 shadow-[0_20px_50px_rgba(0,0,0,0.1)] rounded-3xl overflow-hidden bg-white">
                <div className="flex flex-wrap items-stretch">
                    {/* Left Side: VIP Highlights & Info */}
                    <div className="w-full lg:w-2/5 p-8 md:p-12 bg-[#f8f9fa] flex flex-col justify-center border-r border-gray-100">
                        <div className="mb-8">
                            <span className="inline-block px-4 py-1 rounded-full bg-[#f1ae24]/20 text-[#f1ae24] text-[11px] font-bold mb-4">
                                Premium Service
                            </span>
                            <h2 className="text-2xl md:text-3xl font-bold mb-4 leading-tight text-[#001b4a]">
                                Elite Property <br />
                                <span className="text-[#f1ae24]">Site Preview</span>
                            </h2>
                            <p className="text-[#001b4a] text-[14px] md:text-[16px] leading-relaxed font-medium">
                                Experience excellence with our exclusive site visit service. Get personalized attention and expert insights into your future investment.
                            </p>
                        </div>

                        <div className="space-y-6">
                            {[
                                { icon: '👔', title: 'Expert Guidance', desc: 'Direct access to senior property consultants.' },
                                { icon: '🚗', title: 'Private Logistics', desc: 'Hassle-free transportation for site visits.' },
                                { icon: '📅', title: 'Priority Slot', desc: 'Booking confirmed within 60 minutes.' }
                            ].map((item, index) => (
                                <div key={index} className="flex items-start gap-4 group">
                                    <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center text-[#f1ae24] text-xl transition-all group-hover:bg-[#f1ae24] group-hover:text-white group-hover:shadow-lg">
                                        {item.icon}
                                    </div>
                                    <div>
                                        <h5 className="font-bold text-[#001b4a] mb-1">{item.title}</h5>
                                        <p className="text-[#001b4a] text-[13px] md:text-[14px] leading-relaxed font-medium">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Side: Booking Form */}
                    <div className="w-full lg:w-3/5 p-6 md:p-12 bg-white">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                            <h4 className="text-xl md:text-2xl font-bold text-[#001b4a] mb-0 text-center">Book Property Visit</h4>
                            <div className="flex bg-gray-50 p-1 rounded-xl border border-gray-100 shadow-inner">
                                <button
                                    type="button"
                                    onClick={() => setTourType('In Person')}
                                    className={`px-6 py-2 rounded-lg text-[12px] font-bold transition-all ${tourType === 'In Person' ? 'bg-[#f1ae24] text-white shadow-md' : 'text-[#001b4a]/50 hover:text-[#001b4a]'}`}
                                >
                                    In Person
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setTourType('Video Chat')}
                                    className={`px-6 py-2 rounded-lg text-[12px] font-bold transition-all ${tourType === 'Video Chat' ? 'bg-[#f1ae24] text-white shadow-md' : 'text-[#001b4a]/50 hover:text-[#001b4a]'}`}
                                >
                                    Video Chat
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleTourSubmit}>
                            {/* Date Selector */}
                            <div className="mb-8">
                                <div className="flex items-center justify-between mb-4">
                                    <label className="text-[13px] md:text-[14px] font-bold text-[#001b4a]">1. Select Preferred Date</label>
                                    <input
                                        type="date"
                                        className="bg-gray-50 border border-gray-200 rounded-lg text-[#f1ae24] text-xs font-black px-3 py-1 outline-none cursor-pointer focus:ring-1 focus:ring-[#f1ae24]/30"
                                        min={new Date().toISOString().split('T')[0]}
                                        value={tourDate.toISOString().split('T')[0]}
                                        onChange={(e) => setTourDate(new Date(e.target.value))}
                                    />
                                </div>
                                <div className="flex gap-3 overflow-auto pb-4 no-scrollbar">
                                    {generateTourDates().map((date, idx) => {
                                        const isActive = tourDate.toDateString() === date.toDateString();
                                        return (
                                            <div
                                                key={idx}
                                                onClick={() => setTourDate(date)}
                                                className={`flex-shrink-0 w-20 p-3 rounded-2xl text-center cursor-pointer transition-all border-2 ${isActive ? 'bg-[#f1ae24] border-[#f1ae24] scale-105 shadow-lg' : 'bg-white border-gray-100 hover:border-[#f1ae24]/30'}`}
                                            >
                                                <div className={`text-[12px] font-bold mb-1 ${isActive ? 'text-white' : 'text-[#001b4a]/50'}`}>
                                                    {date.toLocaleDateString('en-US', { weekday: 'short' })}
                                                </div>
                                                <div className={`text-xl font-bold mb-1 ${isActive ? 'text-white' : 'text-[#001b4a]'}`}>
                                                    {date.getDate().toString().padStart(2, '0')}
                                                </div>
                                                <div className={`text-[12px] font-medium ${isActive ? 'text-white/80' : 'text-[#001b4a]/50'}`}>
                                                    {date.toLocaleDateString('en-US', { month: 'short' })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="mb-8">
                                <label className="text-[13px] md:text-[14px] font-bold text-[#001b4a] mb-4 block">2. Contact Details</label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div className="space-y-2">
                                        <select
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-[#001b4a] text-sm font-bold focus:border-[#f1ae24] outline-none transition-all shadow-sm"
                                            required
                                            value={tourForm.time}
                                            onChange={e => setTourForm((prev: any) => ({ ...prev, time: e.target.value }))}
                                        >
                                            <option value="" className="text-[#001b4a]">Select Time</option>
                                            {['10:00 AM', '11:00 AM', '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM', '06:00 PM'].map(t => (
                                                <option key={t} value={t} className="text-[#001b4a]">{t}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <input
                                        type="text"
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-[#001b4a] text-sm font-bold placeholder:text-[#001b4a]/40 focus:border-[#f1ae24] outline-none transition-all shadow-sm"
                                        placeholder="Full Name"
                                        required
                                        value={tourForm.name}
                                        onChange={e => setTourForm((prev: any) => ({ ...prev, name: e.target.value }))}
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <input
                                        type="tel"
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-[#001b4a] text-sm font-bold placeholder:text-[#001b4a]/40 focus:border-[#f1ae24] outline-none transition-all shadow-sm"
                                        placeholder="Phone Number"
                                        required
                                        value={tourForm.phone}
                                        onChange={e => setTourForm((prev: any) => ({ ...prev, phone: e.target.value }))}
                                    />
                                    <input
                                        type="email"
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-[#001b4a] text-sm font-bold placeholder:text-[#001b4a]/40 focus:border-[#f1ae24] outline-none transition-all shadow-sm"
                                        placeholder="Email Address"
                                        value={tourForm.email}
                                        onChange={e => setTourForm((prev: any) => ({ ...prev, email: e.target.value }))}
                                    />
                                </div>
                                <textarea
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-[#001b4a] text-sm font-bold placeholder:text-[#001b4a]/40 focus:border-[#f1ae24] outline-none transition-all shadow-sm"
                                    placeholder="Any special requests? (Optional)"
                                    rows={3}
                                    value={tourForm.message}
                                    onChange={e => setTourForm((prev: any) => ({ ...prev, message: e.target.value }))}
                                ></textarea>
                            </div>

                            <div className="mb-8 flex items-start gap-3">
                                <div className="relative flex items-center">
                                    <input
                                        type="checkbox"
                                        id="terms"
                                        className="hidden peer"
                                        checked={termsAccepted}
                                        onChange={e => setTermsAccepted(e.target.checked)}
                                        required
                                    />
                                    <div 
                                        className="w-5 h-5 border-2 border-gray-200 rounded-md flex items-center justify-center cursor-pointer transition-all peer-checked:bg-[#f1ae24] peer-checked:border-[#f1ae24]"
                                        onClick={() => setTermsAccepted(!termsAccepted)}
                                    >
                                        {termsAccepted && <i className="fa-solid fa-check text-white text-[10px]"></i>}
                                    </div>
                                </div>
                                <label htmlFor="terms" className="text-[11px] text-[#001b4a] font-bold leading-tight cursor-pointer select-none">
                                    I agree to the <Link to="/terms" className="text-[#f1ae24] hover:underline" onClick={(e) => e.stopPropagation()}>Terms of Use</Link> and <Link to="/privacy-policy" className="text-[#f1ae24] hover:underline" onClick={(e) => e.stopPropagation()}>Privacy Policy</Link>. I also authorize My Property Guru to send updates via WhatsApp & SMS.
                                </label>
                            </div>

                            {tourError && (
                                <div className="p-3 mb-6 rounded-xl bg-red-50 border border-red-100 text-red-500 text-xs font-black">
                                    <i className="fa-solid fa-circle-exclamation mr-2"></i>{tourError}
                                </div>
                            )}
                            
                            {tourSuccess && (
                                <div className="p-3 mb-6 rounded-xl bg-green-50 border border-green-100 text-green-500 text-xs font-black">
                                    <i className="fa-solid fa-circle-check mr-2"></i>{tourSuccess}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={tourSubmitting || !termsAccepted}
                                className="w-full relative overflow-hidden py-4 rounded-2xl font-black text-[14px] md:text-[16px] transition-all shadow-xl active:scale-[0.98] bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed border border-[#f1ae24]/30 flex items-center justify-center gap-3 animate-pulse-button"
                            >
                                {/* Continuous Shimmer Effect */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer-continuous pointer-events-none"></div>
                                
                                {tourSubmitting ? (
                                    <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Processing...</>
                                ) : (
                                    <div className="relative z-10 flex items-center justify-center gap-3">
                                        <i className="fa-solid fa-paper-plane text-xs animate-plane-fly"></i>
                                        <span className="uppercase tracking-widest">Confirm Visit</span>
                                    </div>
                                )}
                            </button>

                            <style>{`
                                @keyframes plane-fly {
                                    0% { transform: translate(0, 0) rotate(0); opacity: 1; }
                                    20% { transform: translate(2px, -2px) rotate(-5deg); }
                                    50% { transform: translate(15px, -15px) rotate(-10deg); opacity: 0; }
                                    51% { transform: translate(-15px, 15px) rotate(-10deg); opacity: 0; }
                                    100% { transform: translate(0, 0) rotate(0); opacity: 1; }
                                }
                                .animate-plane-fly {
                                    animation: plane-fly 3s infinite ease-in-out;
                                }
                                @keyframes shimmer-continuous {
                                    0% { transform: translateX(-150%); }
                                    100% { transform: translateX(150%); }
                                }
                                .animate-shimmer-continuous {
                                    animation: shimmer-continuous 3s infinite linear;
                                }
                                @keyframes pulse-button {
                                    0%, 100% { transform: scale(1); box-shadow: 0 10px 15px -3px rgba(5, 20, 75, 0.3); }
                                    50% { transform: scale(1.02); box-shadow: 0 20px 25px -5px rgba(5, 20, 75, 0.4); }
                                }
                                .animate-pulse-button {
                                    animation: pulse-button 3s ease-in-out infinite;
                                }
                            `}</style>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );



};

export default ScheduleTourSection;
